import { LanguageKeys } from '@/contexts/LanguageKeys';
import DOMPurify from 'dompurify';
import { DialogContent } from "../../components/dialog/Dialog";
import { DialogProps } from "../../components/dialog/DialogContext";
import { INTERNAL_SERVER_ERROR, UNAUTHORIZED } from "../../constants/response-status-codes";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Translator } from "../../contexts/translator";
import { ButtonTypes } from "../../models/ButtonTypes";
import { genericError } from "../../models/GenericError";
import { ToastFunction } from '../../models/ReactToastifyType';

// Global translator instance that can be set from components
let globalTranslator: Translator = new Translator(LanguageKeys.english);

export const setGlobalTranslator = (translator: Translator) => {
    globalTranslator = translator;
};

export const getGlobalTranslator = (): Translator  => {
    return globalTranslator;
};

// Utility function to decode HTML entities in a string
const decodeHtmlEntities = (text: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = DOMPurify.sanitize(text);
    textArea.innerHTML = textArea.value;
    return textArea.value;
};

export const getErrorMessage = (error: any, fallback?: string) => {

    // Attempt to extract error message from known error structures
    if (error && error.message) {
        return decodeHtmlEntities(error.message);
    }
    // If error is a string, return it directly
    else if (error && typeof error === 'string') {
        return decodeHtmlEntities(error);
    }

    // If error structure is unknown, return a generic error message or fallback
    return genericError;
}

/**
    Sometimes the exception thrown on backend is a ConstraintViolationException.
    When that happens, the error message is a JSON object with a key 'interpolatedMessage' that contains 
    the actual error message. This function extracts the error message from the JSON object.
    Backend can't intercept this error without adding hooks to all domains.

    If this function does not find the key, it just returns the errDetail as is.
    So should be safe to wrap this around any errors we want to show to the user.
*/
export const getConstraintViolation = (errDetail: any) => {

    if (errDetail) {
        const regex = /interpolatedMessage=(?:'|&#39;)([^'&#39;]+)(?:'|&#39;)/;
        const match = errDetail.match(regex);
        return match ? match[1] : validateGenericErrors(errDetail);
    }
    else {
        return validateGenericErrors(errDetail) ;
    }
}

export const showLoadItemError = (error: any, dialog: DialogProps, translate: Translator, onClick: () => void) => {
    if (isAuthorized(error)) {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.loadItemFailed),
            message: getErrorMessage(error),
            buttons: [{
                id: "btn_BackToMain",
                text: translate.byKey(TranslationKeys.backToMain),
                type: ButtonTypes.BlueRoundedButton,
                onClick
            }]
        }

        dialog?.pushError(dialogContent);
    }
}

export const showError = (error: any, warn: ToastFunction, message?: string) => {
    if (isAuthorized(error)) {
        warn(message ?? getErrorMessage(error));
    }
}

export const processServerError = (error: any) => {

    if (error && error.status) {
        return { status: error.status, message: validateGenericErrors(error.message) ?? genericError }
    }

    return { status: INTERNAL_SERVER_ERROR, message: genericError }
}

/**
 * This function checks for generic errors and returns user-friendly messages.
 * For example, it handles SQL Unique Constraint Violation errors and shows a translated message if available.
 * If no translator is available, it returns the original message as-is.
 * @param message - The error message to validate and potentially translate
 * @returns The translated message if available, otherwise the original message
 */
export const validateGenericErrors = (message: any) => {

    // SQL Unique Constraint Violation
    if (message && typeof message === 'string' && message.includes('Violation of UNIQUE KEY constraint')) {
        const translator = getGlobalTranslator();
        return translator ? translator.byKey(TranslationKeys.duplicateEntryNotAllowed) : message;
    }

    return message; // Just return as is. The caller will handle this.
}

const isAuthorized = (error: any) => {
    const token = sessionStorage.getItem('token');
    return error && error.status && error.status !== UNAUTHORIZED && token !== null;
}
